import React, { useContext, useState } from 'react';
import PriceCard from '../components/PriceCard/PriceCard';
import { GeneralContext } from '../../../GeneralProvider';
import AlertBox from '../../../components/AlertBox/AlertBox';
import Services from '../../../services/Services';
import { useLanguage } from '../../../LanguageProvider';

export default function SelectPlan({
    setCurrentPage,
    setSpecs,
    setSelectedTitle,
    setSelectedPrice,
    userCountry,
    setSelectedPriceNumeric,
    setSelectedLabel
}) {
    const { getTranslation } = useLanguage();
    const { subscriptionRenewDate, subscriptionType, userId, setSubscriptionToggle, subscriptionToggle } = useContext(GeneralContext);
    const token = localStorage.getItem("token");
    const [isSocialHiveAlertOpen, setIsSocialHiveAlertOpen] = useState(false);
    const [pendingPlan, setPendingPlan] = useState(null);

    const handleConfirmSocialHive = () => {
        Services.UpdateUserSubscriptionType(token, userId, "social-hive")
            .then((res) => {
                if (res.data.status) {
                    setSubscriptionToggle(!subscriptionToggle)
                }
            })
            .catch((err) => console.log(err));

        setIsSocialHiveAlertOpen(false);
        setPendingPlan(null);
    };

    const handleCancelSocialHive = () => {
        setIsSocialHiveAlertOpen(false);
        setPendingPlan(null);
    };

    const formatTimestamp = () => {
        if (!subscriptionRenewDate && subscriptionRenewDate !== 0) return '-';
        const date = new Date(Number(subscriptionRenewDate) * 1000);
        if (isNaN(date.getTime())) return '-';
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}.${month}.${year}`;
    };

    function capitalizeWords(text) {
        if (!text) return '';
        return text
            .split(/[\s-]+/) // hem boşluk hem tireye göre ayır
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    }

    const plans = [
        {
            title: 'Social Hive',
            label: 'social-hive',
            priceTR: '0₺',
            priceEN: '0$',
            numericTR: 0,
            numericEN: 0,
            specs: [
                getTranslation("joinChannels"),
                getTranslation("chatAndShare"),
                getTranslation("checkEvents"),
                getTranslation("accessAIHelper")
            ],
        },
        {
            title: 'Basic',
            label: 'basic',
            priceTR: '399₺',
            priceEN: '10$',
            numericTR: 399,
            numericEN: 10,
            specs: [
                getTranslation("workspaceManager"),
                getTranslation("taskManagement"),
                getTranslation("apiPostGet"),
                getTranslation("kronos"),
                getTranslation("apiDocumentation"),
                getTranslation("notebook"),
                getTranslation("plusAllSocialHive")
            ],
        },
        {
            title: 'Professional',
            label: 'professional',
            priceTR: '1199₺',
            priceEN: '30$',
            numericTR: 1199,
            numericEN: 30,
            specs: [
                getTranslation("leadCommunity"),
                getTranslation("createChannels"),
                getTranslation("showcaseGallery"),
                getTranslation("hostEvents"),
                getTranslation("sellTickets"),
                getTranslation("plusAllBasicAndSocialHive")
            ],
        },
    ];

    const toSlug = (s) =>
        s?.toString().trim().toLowerCase().replace(/[\s_]+/g, '-') ?? '';

    const subSlug = toSlug(subscriptionType);
    const currentPlan = plans.find(
        (p) => p.label === subSlug || toSlug(p.title) === subSlug
    );

    const isSocialHive = subSlug === 'social-hive' || currentPlan?.label === 'social-hive';

    const handlePlanClick = (plan) => {
        const price = userCountry === 'Turkey' ? plan.priceTR : plan.priceEN;
        const numericPrice = userCountry === 'Turkey' ? plan.numericTR : plan.numericEN;

        // Social Hive seçilirse Alert aç, ödeme sayfasına gitme
        if (toSlug(plan.label) === 'social-hive' || toSlug(plan.title) === 'social-hive') {
            setPendingPlan(plan);
            setIsSocialHiveAlertOpen(true);
            return;
        }

        // Diğer planlarda önceki akış: ödeme sayfasına yönlendir
        setSelectedPriceNumeric(numericPrice);
        setSelectedTitle(plan.title);
        setSelectedPrice(price);
        setCurrentPage('payment');
        setSpecs(plan.specs);
        setSelectedLabel(plan.label)
    };

    return (
        <div className="custom-plan-container">
            <div className="custom-plan-content">
                <div className="custom-plan-inner-content">
                    <span className="custom-plan-title">{getTranslation("viewPlans")}</span>
                    <span className="custom-plan-subtitle">{getTranslation("choosePlan")}</span>

                    <span className="custom-plan-row">
                        <span className="custom-plan-row-title">{getTranslation("plan")}</span>
                        <span className="custom-plan-row-text">
                            {capitalizeWords(subscriptionType)}
                        </span>
                    </span>

                    <span className="custom-plan-row">
                        <span className="custom-plan-row-title">{getTranslation("subscription")}</span>
                        {userCountry && (
                            <span className="custom-plan-row-text">
                                {currentPlan
                                    ? userCountry === 'Turkey'
                                        ? `${currentPlan.priceTR}/month`
                                        : `${currentPlan.priceEN}/month`
                                    : '-'}
                                {isSocialHive ? ' (Free)' : ''}
                            </span>
                        )}
                    </span>

                    <span className="custom-plan-row">
                        <span className="custom-plan-row-title">{getTranslation("renewDate")}</span>
                        <span className="custom-plan-row-text">{formatTimestamp()}</span>
                    </span>

                    {userCountry && (
                        <div
                            style={{
                                width: '100%',
                                alignItems: 'center',
                                justifyContent: 'center',
                                display: 'flex',
                                marginTop: '21px',
                                gap: '30px',
                            }}
                        >
                            {plans.map((plan, index) => {
                                const price = userCountry === 'Turkey' ? plan.priceTR : plan.priceEN;
                                const isCurrentPlan =
                                    toSlug(subscriptionType) === plan.label ||
                                    toSlug(subscriptionType) === toSlug(plan.title);

                                return (
                                    <PriceCard
                                        key={index}
                                        title={plan.title}
                                        price={price}
                                        specs={plan.specs}
                                        isCurrent={isCurrentPlan}
                                        onClick={() => handlePlanClick(plan)}
                                    />
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            <AlertBox
                isOpen={isSocialHiveAlertOpen}
                type="warning"
                title={getTranslation("planChange")}
                message={getTranslation("planChangeConfirm")}
                buttons={[
                    {
                        text: getTranslation("no"),
                        styleType: 'alert-cancel',
                        onClick: handleCancelSocialHive,
                    },
                    {
                        text: getTranslation("yes"),
                        styleType: 'alert-confirm',
                        onClick: handleConfirmSocialHive,
                    },
                ]}
            />
        </div>
    );
}